import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Theme types
type ThemeMode = 'light' | 'dark' | 'system';
type ThemeColor = 'default' | 'islamic' | 'desert' | 'ocean';

interface ThemeConfig {
  mode: ThemeMode;
  color: ThemeColor;
  fontSize: 'small' | 'medium' | 'large';
  reducedMotion: boolean;
  highContrast: boolean;
}

interface ThemeContextType {
  theme: ThemeConfig;
  setMode: (mode: ThemeMode) => void;
  setColor: (color: ThemeColor) => void;
  setFontSize: (size: ThemeConfig['fontSize']) => void;
  setReducedMotion: (reduced: boolean) => void;
  setHighContrast: (high: boolean) => void;
  isDark: boolean;
  toggleMode: () => void;
}

const defaultTheme: ThemeConfig = {
  mode: 'light',
  color: 'default',
  fontSize: 'medium',
  reducedMotion: false,
  highContrast: false,
};

const ThemeContext = createContext<ThemeContextType>({
  theme: defaultTheme,
  setMode: () => {},
  setColor: () => {},
  setFontSize: () => {},
  setReducedMotion: () => {},
  setHighContrast: () => {},
  isDark: false,
  toggleMode: () => {},
});

const THEME_STORAGE_KEY = 'hudan-theme';

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<ThemeConfig>(() => {
    // Load theme from localStorage on initial render
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored) {
        try {
          return { ...defaultTheme, ...JSON.parse(stored) };
        } catch {
          return defaultTheme;
        }
      }
    }
    return defaultTheme;
  });

  const [isDark, setIsDark] = useState(false);

  // Determine actual dark mode based on theme.mode and system preference
  useEffect(() => {
    const updateDarkMode = () => {
      if (theme.mode === 'system') {
        const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setIsDark(systemDark);
      } else {
        setIsDark(theme.mode === 'dark');
      }
    };

    updateDarkMode();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme.mode === 'system') {
        updateDarkMode();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme.mode]);

  // Apply theme classes to document
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Dark mode
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Color theme
    root.classList.remove('theme-islamic', 'theme-desert', 'theme-ocean');
    if (theme.color !== 'default') {
      root.classList.add(`theme-${theme.color}`);
    }

    // Font size
    root.classList.remove('text-sm', 'text-base', 'text-lg');
    const fontSizeClass = {
      small: 'text-sm',
      medium: 'text-base',
      large: 'text-lg',
    }[theme.fontSize];
    body.classList.add(fontSizeClass);

    // Reduced motion
    if (theme.reducedMotion) {
      root.classList.add('motion-reduce');
    } else {
      root.classList.remove('motion-reduce');
    }

    // High contrast
    if (theme.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Save to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
  }, [theme, isDark]);

  // Check for user's motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches && !theme.reducedMotion) {
      setTheme(prev => ({ ...prev, reducedMotion: true }));
    }
  }, []);

  const setMode = (mode: ThemeMode) => {
    setTheme(prev => ({ ...prev, mode }));
  };

  const setColor = (color: ThemeColor) => {
    setTheme(prev => ({ ...prev, color }));
  };

  const setFontSize = (fontSize: ThemeConfig['fontSize']) => {
    setTheme(prev => ({ ...prev, fontSize }));
  };

  const setReducedMotion = (reducedMotion: boolean) => {
    setTheme(prev => ({ ...prev, reducedMotion }));
  };

  const setHighContrast = (highContrast: boolean) => {
    setTheme(prev => ({ ...prev, highContrast }));
  };

  const toggleMode = () => {
    setTheme(prev => ({
      ...prev,
      mode: prev.mode === 'light' ? 'dark' : prev.mode === 'dark' ? 'system' : 'light',
    }));
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setMode,
        setColor,
        setFontSize,
        setReducedMotion,
        setHighContrast,
        isDark,
        toggleMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export type { ThemeMode, ThemeColor, ThemeConfig };
